import { useEffect, useRef, useState } from 'react';

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

const LOOP_RESET_MS = 480;

export function usePlayback(active, { steps, stepDelays, loopPause, offsetMs = 0 }, reducedMotion, loop = true) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [resetting, setResetting] = useState(false);

  // Run playback steps for each cycle
  useEffect(() => {
    if (!active) {
      setVisibleCount(0);
      setResetting(false);
      return undefined;
    }

    if (reducedMotion) {
      setVisibleCount(steps.length);
      setResetting(false);
      return undefined;
    }

    if (resetting) return undefined;

    setVisibleCount(0);
    const timers = stepDelays.map((delay, index) =>
      setTimeout(() => setVisibleCount(index + 1), offsetMs + delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [active, reducedMotion, cycle, resetting, steps.length, stepDelays, offsetMs]);

  // After full playback, pause then rewind before the next cycle
  useEffect(() => {
    if (!active || reducedMotion || !loop || resetting) return undefined;
    if (visibleCount !== steps.length) return undefined;

    const loopTimer = setTimeout(() => setResetting(true), loopPause);
    return () => clearTimeout(loopTimer);
  }, [active, reducedMotion, loop, resetting, visibleCount, steps.length, loopPause]);

  // Finish rewind and start the next cycle
  useEffect(() => {
    if (!resetting) return undefined;

    const resetTimer = setTimeout(() => {
      setResetting(false);
      setCycle((value) => value + 1);
    }, LOOP_RESET_MS);

    return () => clearTimeout(resetTimer);
  }, [resetting]);

  const isVisible = (step) => {
    const index = steps.indexOf(step);
    return index >= 0 && visibleCount > index;
  };

  return { isVisible, visibleCount, cycle, resetting };
}

/** Ease-in-out quint: gentler than native scrollTo smooth */
function easeInOutQuint(t) {
  return t < 0.5 ? 16 * t ** 5 : 1 - (-2 * t + 2) ** 5 / 2;
}

export function useSmoothCoworkScroll(scrollRef, visibleCount, reducedMotion, { cycle = 0, resetting = false } = {}) {
  const rafRef = useRef(null);

  // Rewind scroll while content is still visible at end of loop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !resetting) return undefined;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (reducedMotion) {
      el.scrollTop = 0;
      return undefined;
    }

    const start = el.scrollTop;
    if (start < 1) return undefined;

    const duration = Math.min(LOOP_RESET_MS - 40, Math.max(280, start * 0.4));
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      el.scrollTop = start * (1 - easeInOutQuint(progress));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [resetting, reducedMotion, scrollRef]);

  // Advance scroll as new blocks appear
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || visibleCount === 0 || resetting) return undefined;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const scrollToBottom = () => {
      const target = Math.max(0, el.scrollHeight - el.clientHeight);
      if (reducedMotion) {
        el.scrollTop = target;
        return;
      }

      const start = el.scrollTop;
      const distance = target - start;
      if (Math.abs(distance) < 1) return;

      const duration = Math.min(1200, Math.max(650, Math.abs(distance) * 0.85));
      const startTime = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        el.scrollTop = start + distance * easeInOutQuint(progress);
        if (progress < 1) rafRef.current = requestAnimationFrame(step);
      };

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(scrollToBottom);
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visibleCount, reducedMotion, scrollRef, resetting, cycle]);
}
